"use memo"

import { ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import {
  SetupNotificationsLayout,
  HubEventHandler,
} from 'layout/SetupNotificationsLayout'
import { HUB_NAMES } from 'constants/signalrConstants'
import {
  NotificationType,
  CampaignUpdatedEvent,
  RexListingCreatedEvent,
  TaskUpdatedNotification,
  PdfProcessedEvent,
  TaskCreatedNotification,
  ContractDocumentUpdatedNotification,
} from 'types/pushNotifications'
import { emitTaskUpdate } from '../hooks/useAutopilotTaskUpdates'
import {
  handleCampaignUpdated,
  handleRexListingCreated,
} from './appNotificationsHandlers'
import { createCampaignDetailsQueryKey } from 'hooks/useSelectedCampaign'
import { emitPdfProcessedEvent } from 'hooks/usePdfProcessedEvent'
import { emitTaskCreatedEvent } from 'hooks/useTaskCreatedEvent'
import {
  useInvalidateUseGetArchivedCampaignsByAdmin,
  useInvalidateUseGetCampaignsForAgency,
} from '@urbanx/agx.campaigns.client'
import type { AutopilotTaskDto } from '@urbanx/agx.activities.client'
import { useInvalidateUseGetListingByCampaignId } from '@urbanx/agx.rex.client'
import { applyTaskUpdateToTasks } from 'utils/tasks/taskUpdates'

interface SignalrCommsProps {
  children: ReactNode
}

const handleAppNotificationsConnected = () =>
  console.log('AppNotifications hub connected')

const handleAutopilotTasksConnected = () =>
  console.log('AutopilotTasks hub connected')

const isAutopilotTasksQueryForCampaign = (
  queryKey: readonly unknown[],
  campaignId: string
) =>
  queryKey[0] === 'getAutopilotTasksForCampaign' && queryKey[1] === campaignId

const SignalrComms = ({ children }: SignalrCommsProps) => {
  const queryClient = useQueryClient()
  const invalidateCampaignListing = useInvalidateUseGetListingByCampaignId()
  const invalidateCampaigns = useInvalidateUseGetCampaignsForAgency()
  const invalidateArchivedCampaigns =
    useInvalidateUseGetArchivedCampaignsByAdmin()
  const invalidateSelectedCampaign = async (campaignId: string) => {
    await queryClient.invalidateQueries({
      queryKey: createCampaignDetailsQueryKey(campaignId),
    })
  }
  const invalidateCampaignScopedQueries = async (campaignId: string) => {
    await queryClient.invalidateQueries({
      predicate: (query) =>
        Array.isArray(query.queryKey) && query.queryKey.includes(campaignId),
    })
  }
  const handleTaskUpdated = async (event: TaskUpdatedNotification) => {
    emitTaskUpdate(event)

    const campaignId = event.task.campaignId
    if (!campaignId) return

    queryClient.setQueriesData<AutopilotTaskDto[]>(
      {
        predicate: (query) =>
          isAutopilotTasksQueryForCampaign(query.queryKey, campaignId),
      },
      (previousTasks) =>
        previousTasks ? applyTaskUpdateToTasks(previousTasks, event) : previousTasks
    )

    await queryClient.invalidateQueries({
      predicate: (query) =>
        isAutopilotTasksQueryForCampaign(query.queryKey, campaignId),
    })
  }

  const appNotificationHandlers: HubEventHandler[] = [
    {
      eventType: NotificationType.CampaignUpdatedEvent,
      handler: (event: CampaignUpdatedEvent) =>
        handleCampaignUpdated(
          event,
          invalidateCampaignScopedQueries,
          invalidateSelectedCampaign,
          [invalidateCampaigns, invalidateArchivedCampaigns]
        ),
    },
    {
      eventType: NotificationType.PdfProcessedEvent,
      handler: (event: PdfProcessedEvent) => emitPdfProcessedEvent(event),
    },
    {
      eventType: NotificationType.RexListingCreatedEvent,
      handler: (event: RexListingCreatedEvent) =>
        handleRexListingCreated(event, invalidateCampaignListing),
    },
  ]

  const autopilotTaskHandlers: HubEventHandler[] = [
    {
      eventType: NotificationType.TaskUpdatedNotification,
      handler: (event: TaskUpdatedNotification) => handleTaskUpdated(event),
    },
    {
      eventType: NotificationType.TaskCreatedNotification,
      handler: (event: TaskCreatedNotification) => emitTaskCreatedEvent(event),
    },
    {
      eventType: NotificationType.ContractDocumentUpdatedNotification,
      handler: async (event: ContractDocumentUpdatedNotification) => {
        if (!event.campaignId) return

        await Promise.all([
          invalidateCampaignScopedQueries(event.campaignId),
          invalidateSelectedCampaign(event.campaignId),
        ])
      },
    },
  ]
  return (
    <SetupNotificationsLayout
      hubName={HUB_NAMES.APP_NOTIFICATIONS}
      eventHandlers={appNotificationHandlers}
      onConnected={handleAppNotificationsConnected}
    >
      <SetupNotificationsLayout
        hubName={HUB_NAMES.AUTOPILOT_TASKS}
        eventHandlers={autopilotTaskHandlers}
        onConnected={handleAutopilotTasksConnected}
      >
        {children}
      </SetupNotificationsLayout>
    </SetupNotificationsLayout>
  )
}

export default SignalrComms
